<template>
  <!--
    DsButton — implementación Vue del button del DS (elements/button/button.css).
    Consume TOKENS del DS; estilo scoped con clase propia `ds-btn` para no chocar
    con la `.btn` legacy (430 usos en la app). Ver docs/component-architecture.md.
    Roles (ADR 0025 + 0030): feature = CTA de marca (uno por vista) · primary = sólido
    ink · secondary = outline · danger = destructivo grave (fill) · danger-subtle =
    destructivo rutinario (ghost).
  -->
  <component
    :is="tag"
    class="ds-btn"
    :class="[
      `ds-btn--${variant}`,
      size !== 'md' && `ds-btn--${size}`,
      { 'is-loading': loading, 'has-trailing': hasTrailing },
    ]"
    :type="tag === 'button' ? type : undefined"
    :href="tag === 'a' ? href : undefined"
    :disabled="tag === 'button' ? (disabled || loading) : undefined"
    :aria-disabled="tag === 'a' && (disabled || loading) ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
  >
    <span class="ds-btn__label"><slot /></span>
    <span v-if="hasTrailing" class="ds-btn__trailing"><slot name="trailing" /></span>
  </component>
</template>

<script>
export default {
  name: 'DsButton',
  props: {
    /** Rol del botón (ADR 0025). */
    variant: {
      type: String,
      default: 'primary',
      validator: (v) => ['feature', 'primary', 'secondary', 'danger', 'danger-subtle'].includes(v),
    },
    /** Tamaño. */
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    /** type nativo cuando renderiza <button>. */
    type: { type: String, default: 'button' },
    /** Si se pasa href, renderiza como <a>. */
    href: { type: String, default: null },
  },
  computed: {
    tag() {
      return this.href ? 'a' : 'button';
    },
    hasTrailing() {
      return !!this.$slots.trailing;
    },
  },
};
</script>

<style scoped lang="scss">
/* Base — fiel a .btn del DS: Geist, normal case, weight 500, radius sm. */
.ds-btn {
  font-family: var(--font-sans);
  font-size: var(--text-body-s-size);
  font-weight: 500;
  text-transform: none;
  line-height: 20px;
  padding: 14px 18px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-decoration: none;
  cursor: pointer;
  background: none;
  color: inherit;
  transition:
    background-color var(--duration-state, 140ms) var(--ease-default, ease),
    border-color var(--duration-state, 140ms) var(--ease-default, ease),
    color var(--duration-state, 140ms) var(--ease-default, ease),
    opacity var(--duration-base, 240ms) var(--ease-default, ease);
  outline: none;
}

/* Label + trailing icon van SIEMPRE juntos y centrados (el `justify-content:
   center` de arriba, wide o no): el gap los mantiene pegados como una unidad, así
   un botón ancho lleva la flecha al lado del texto y no colgada del borde.
   El DS quitó el auto-justify en 0.1.16 — el idiom "texto a la izquierda / icono
   al borde" vive en Link, que es su dueño; en Button duplicaba el modelo. Si algún
   día hace falta el spread, el DS agrega `.btn-spread`. */
.ds-btn__trailing {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  :deep(svg) { width: 16px; height: 16px; }
}

/* Sizes — solo padding + gap + line-height (mismo font/case). */
.ds-btn--sm { padding: 10px 14px; gap: 8px; line-height: 16px; }
.ds-btn--lg { padding: 18px 22px; gap: 12px; line-height: 22px; }

/* ── Variants ──────────────────────────────────────────────────
   primary/secondary son surface-aware SOLOS: --text-default y
   --text-inverse cascadean (ink+vellum en claro → vellum+ink en
   oscuro). feature (teal fijo) sí necesita override en oscuro. */
.ds-btn--primary {
  background: var(--text-default);
  color: var(--text-inverse);
  border-color: var(--text-default);
}
.ds-btn--feature {
  background: var(--teal-700);
  color: var(--vellum);
  border-color: var(--teal-700);
}
.ds-btn--secondary {
  background: transparent;
  color: var(--text-default);
  border-color: var(--text-muted);
}

/* Danger (ADR 0030) — dos pesos, espejo de `elements/button/button.css`:
   · danger        = fill grave. `--error-500` es CONSTANTE en toda surface (los
                     steps de --error-* no cascadean), y el label va en `--vellum`
                     literal: 4.77:1, el piso de AA a 14px. Por eso NO lleva
                     override en oscuro, a diferencia de feature.
   · danger-subtle = ghost rutinario. Es surface-aware solo porque sus tokens ya
                     cascadean (`--text-error` es error-700 en claro y error-300 en
                     slate, espejado en 0.1.15); el borde `--border-error` es el
                     ancla y no se mueve. */
.ds-btn--danger {
  background: var(--error-500);
  color: var(--vellum);
  border-color: var(--error-500);
}
.ds-btn--danger-subtle {
  background: transparent;
  color: var(--text-error);
  border-color: var(--border-error);
}

/* feature sobre superficie oscura converge a primary (ADR 0025) → en el bloque NO-scoped
   del final (el scoped no puede targetear el ancestro [data-surface]). */

/* ── Hover — "towards center" (Modelo A), solo en devices con hover. */
@media (hover: hover) and (pointer: fine) {
  .ds-btn--primary:hover:not(:disabled) {
    background: var(--slate-800);
    border-color: var(--slate-800);
  }
  .ds-btn--feature:hover:not(:disabled) {
    background: var(--teal-600);
    border-color: var(--teal-600);
  }
  /* wash basado en --text-default → cascadea (ink en claro, vellum en oscuro). */
  .ds-btn--secondary:hover:not(:disabled) {
    background: color-mix(in srgb, var(--text-default) 4%, transparent);
    border-color: color-mix(in oklch, var(--text-muted) 50%, var(--text-default) 50%);
  }
  /* Danger OSCURECE en hover — dirección invertida respecto del resto, que va
     "towards center". Es deliberado y está documentado en el DS: la acción es
     irreversible, así que el feedback intensifica en vez de suavizar. */
  .ds-btn--danger:hover:not(:disabled) {
    background: var(--error-600);
    border-color: var(--error-600);
  }
  .ds-btn--danger-subtle:hover:not(:disabled) {
    background: color-mix(in oklch, var(--error-500) 10%, transparent);
    border-color: var(--error-600);
  }
}

/* ── Focus (teclado). */
.ds-btn:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 3px;
}

/* ── Active (tap). */
.ds-btn--primary:active:not(:disabled) {
  background: var(--slate-700);
  border-color: var(--slate-700);
  transition-duration: var(--duration-tap, 80ms);
}
.ds-btn--feature:active:not(:disabled) {
  background: var(--teal-500);
  border-color: var(--teal-500);
  transition-duration: var(--duration-tap, 80ms);
}
.ds-btn--secondary:active:not(:disabled) {
  background: color-mix(in srgb, var(--text-default) 8%, transparent);
  transition-duration: var(--duration-tap, 80ms);
}
/* Danger active: sigue oscureciendo (error-700, label 9.66:1). */
.ds-btn--danger:active:not(:disabled) {
  background: var(--error-700);
  border-color: var(--error-700);
  transition-duration: var(--duration-tap, 80ms);
}
.ds-btn--danger-subtle:active:not(:disabled) {
  background: color-mix(in oklch, var(--error-500) 16%, transparent);
  border-color: var(--error-700);
  transition-duration: var(--duration-tap, 80ms);
}

/* ── Disabled — tokens sólidos, convergen todas las variants. */
.ds-btn:disabled,
.ds-btn[aria-disabled="true"] {
  background: var(--surface-disabled);
  color: var(--text-disabled);
  border-color: var(--border-disabled);
  cursor: not-allowed;
}
/* Los ghost no tienen fill por default → se mantienen transparent en disabled. */
.ds-btn--secondary:disabled,
.ds-btn--secondary[aria-disabled="true"],
.ds-btn--danger-subtle:disabled,
.ds-btn--danger-subtle[aria-disabled="true"] {
  background: transparent;
}

/* ── Loading — tres puntos con pulse de opacidad (sin movimiento). */
.ds-btn.is-loading {
  pointer-events: none;
}
.ds-btn.is-loading .ds-btn__trailing { display: none; }
.ds-btn.is-loading .ds-btn__label { opacity: 0.7; }
.ds-btn.is-loading::after {
  content: '';
  display: inline-block;
  width: 32px;
  height: 8px;
  margin-left: 10px;
  background-image:
    radial-gradient(circle at 4px 4px, currentColor 3px, transparent 3.5px),
    radial-gradient(circle at 16px 4px, currentColor 3px, transparent 3.5px),
    radial-gradient(circle at 28px 4px, currentColor 3px, transparent 3.5px);
  background-repeat: no-repeat;
  animation: ds-btn-dot-pulse 1.2s ease-in-out infinite;
  opacity: 0.4;
}
@keyframes ds-btn-dot-pulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.9; }
}
@media (prefers-reduced-motion: reduce) {
  .ds-btn.is-loading::after { animation: none; }
}
</style>

<!--
  Bloque NO-scoped: overrides que dependen del ANCESTRO [data-surface]. El scoped NO puede
  targetear un ancestro — Vue compila `:deep([data-surface]) .ds-btn` como
  `[data-v] [data-surface] .ds-btn` (scope-attr como ancestro), que nunca matchea porque el
  data-v vive en el botón. Por eso va acá, global.
-->
<style lang="scss">
/* Convergencia de feature → primary (ink) sobre superficie oscura (ADR 0025): en immersive/chrome
   el feature deja el teal y adopta el tratamiento primary (vellum + ink), igual que .ds-btn--primary.
   Doble clase para ganar especificidad a la base scoped del feature. */
[data-surface="immersive"] .ds-btn.ds-btn--feature:not(:disabled),
[data-surface="chrome"] .ds-btn.ds-btn--feature:not(:disabled) {
  background: var(--text-default);
  color: var(--text-inverse);
  border-color: var(--text-default);
}

/* Superficie oscura: primary (y feature convergido) es VELLUM (bg = --text-default). El hover/active
   base (slate-800/700 · teal) lo oscurecía/lo sacaba de tono → "towards center" correcto = vellum un
   paso más oscuro. Doble clase .ds-btn.ds-btn--X gana especificidad al hover scoped.
   Arregla de raíz el hero del dashboard y /ds (el override externo del hero quedó redundante). */
@media (hover: hover) and (pointer: fine) {
  [data-surface="immersive"] .ds-btn.ds-btn--primary:hover:not(:disabled),
  [data-surface="chrome"] .ds-btn.ds-btn--primary:hover:not(:disabled),
  [data-surface="immersive"] .ds-btn.ds-btn--feature:hover:not(:disabled),
  [data-surface="chrome"] .ds-btn.ds-btn--feature:hover:not(:disabled) {
    background: var(--vellum-200);
    border-color: var(--vellum-200);
  }
}
[data-surface="immersive"] .ds-btn.ds-btn--primary:active:not(:disabled),
[data-surface="chrome"] .ds-btn.ds-btn--primary:active:not(:disabled),
[data-surface="immersive"] .ds-btn.ds-btn--feature:active:not(:disabled),
[data-surface="chrome"] .ds-btn.ds-btn--feature:active:not(:disabled) {
  background: var(--vellum-300);
  border-color: var(--vellum-300);
  transition-duration: var(--duration-tap, 80ms);
}
</style>
